import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import UserAvatar from "@/components/UserAvatar";
import UploadButton from "@/components/UploadButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, Circle, Clock, Camera, BedDouble, HeartPulse } from "lucide-react";
import { toast } from "sonner";

const CAT_COLORS = { medication: "#EF4444", hygiene: "#06B6D4", meal: "#F59E0B", cleaning: "#10B981",
  wellness: "#3B82F6", appointment: "#8B5CF6", behaviour: "#EC4899", documentation: "#64748B", general: "#64748B" };

export default function Care() {
  const { t } = useT();
  const [tasks, setTasks] = useState([]);
  const [dlg, setDlg] = useState({ open: false, task: null });
  const [note, setNote] = useState(""); const [photo, setPhoto] = useState(null); const [issue, setIssue] = useState("");

  const load = () => api.get("/residents/care/today").then((r) => setTasks(r.data));
  useEffect(() => { load(); }, []);

  const openComplete = (task) => { setNote(""); setPhoto(null); setIssue(""); setDlg({ open: true, task }); };
  const submit = async () => {
    try { await api.post(`/residents/tasks/${dlg.task.id}/complete`, { note, photo, issue: issue || null }); toast.success(t("markComplete")); setDlg({ open: false, task: null }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold tracking-tight">{t("careToday")}</h1>
        <p className="text-muted-foreground mt-1">{new Date().toLocaleDateString()}</p>
      </div>
      {tasks.length === 0 && (
        <Card className="p-12 text-center text-muted-foreground">
          <HeartPulse className="w-10 h-10 mx-auto mb-3 opacity-40" />
          {t("noCareTasks")}
        </Card>
      )}
      <div className="space-y-3">
        {tasks.map((tk, i) => (
          <Card key={tk.id} className="p-4 flex items-start gap-3 animate-fade-up" style={{ animationDelay: `${i * 30}ms` }} data-testid="care-task">
            {tk.status === "completed"
              ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
              : <button onClick={() => openComplete(tk)} data-testid="open-complete-care"><Circle className="w-5 h-5 text-muted-foreground hover:text-primary mt-0.5" /></button>}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className={`font-medium ${tk.status === "completed" ? "line-through text-muted-foreground" : ""}`}>{tk.title}</p>
                <span className="text-[10px] px-2 py-0.5 rounded-full text-white" style={{ background: CAT_COLORS[tk.category] || "#64748B" }}>{t(tk.category)}</span>
                {tk.require_photo && <Badge variant="outline" className="text-[10px]"><Camera className="w-3 h-3 mr-1" />{t("requirePhoto")}</Badge>}
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-sm text-muted-foreground">
                {tk.resident && <span className="flex items-center gap-1.5"><UserAvatar name={tk.resident.full_name} src={tk.resident.photo} size={22} />{tk.resident.full_name} · <BedDouble className="w-3.5 h-3.5" />{tk.resident.room_number}</span>}
                {tk.time && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{tk.time}</span>}
              </div>
              {tk.note && <p className="text-xs text-muted-foreground mt-1">📝 {tk.note}</p>}
            </div>
            {tk.status !== "completed" && <Button size="sm" variant="outline" onClick={() => openComplete(tk)} data-testid="complete-care">{t("markComplete")}</Button>}
          </Card>
        ))}
      </div>

      <Dialog open={dlg.open} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{dlg.task?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">{t("completionNote")}</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="care-note" /></div>
            <div className="flex items-center gap-2">
              <UploadButton label={t("uploadProof")} accept="image/*" category="resident-task" testId="care-upload"
                onUploaded={(d) => { setPhoto(`file:${d.id}`); toast.success("✓"); }} />
              {photo && <Badge variant="secondary"><Camera className="w-3 h-3 mr-1" />1</Badge>}
            </div>
            <div className="space-y-1.5"><Label className="text-xs">{t("reportIssue")} ({t("optional")})</Label><Textarea rows={2} value={issue} onChange={(e) => setIssue(e.target.value)} data-testid="care-issue" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg({ open: false, task: null })}>{t("cancel")}</Button>
            <Button onClick={submit} disabled={dlg.task?.require_photo && !photo} data-testid="submit-care">{t("confirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
